import { CountingCirclesRecord } from 'api/api.types';
import ApiContext from 'api/ApiContext';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { MuniTab } from 'lib/MapSwitch/MapSwitch';
import getMapColorProportional from './getMapColorProportional';
import getMapColorPoll from './getMapColorPoll';

// VOTERS_ABROAD_BFS_NR has been changed from 9190 to 19190 in April 2023
// Bfs_numbers are not changed retroactively for older issues.
export const VOTERS_ABROAD_BFS_NRS = ['19190', '9190'];

const fetchMap = async (year: string | number, mapUrl: string) => {
  const response = await fetch(`${mapUrl}${year}`);
  return response.text();
};

interface Props {
  year?: string | number;
  records: CountingCirclesRecord[];
  type: 'proportional' | 'poll';
  selectedMuniTab?: MuniTab;
}

const useMapApi = ({ year, records, type, selectedMuniTab }: Props) => {
  const [hoveredElement, setHoveredElement] = useState<CountingCirclesRecord | null>(
    null,
  );
  const [selectedElement, setSelectedElement] = useState<CountingCirclesRecord | null>(
    null,
  );

  const queryRoot =
    document.querySelector(`[data-${process.env.REACT_APP_NAME}]`).shadowRoot ?? document;

  const { mapUrl } = useContext(ApiContext);

  const query = useQuery(['map', year], () => fetchMap(year, mapUrl), {
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  useEffect(() => {
    // Remove selected class for all elements with .map--initialized
    Array.from(queryRoot.querySelectorAll('.map--initialized')).forEach((path) =>
      path.classList.remove('map--selected'),
    );

    // Add selected class only for the element with the correct bfs_number
    if (selectedElement) {
      Array.from(
        queryRoot.querySelectorAll(`[agis\\:gmdnr="${selectedElement.bfs_number}"]`),
      ).forEach((element) => element.classList.add('map--selected'));
    }
  }, [selectedElement]);

  const onSelectMunicipality = (
    muniElement: SVGPathElement,
    record: CountingCirclesRecord,
  ) => {
    const bfsNr = muniElement.getAttribute('agis:gmdnr');

    // add bfs number to record if not already present
    const element = {
      bfs_number: bfsNr,
      ...record,
    };

    setSelectedElement(element);
  };

  // Match names from archive to names from map
  const unifyMuniciapalityName = (name: string) => {
    switch (name) {
      case 'Wil AG':
        return 'Wil';
      case 'Hausen AG':
        return 'Hausen';
      case 'Beinwil/Freiamt': {
        if (Number(year) < 2010) {
          return 'Beinwil-Freiamt';
        }
        return 'Beinwil (Freiamt)';
      }
      default:
        return name;
    }
  };

  const removeAllHighlights = () => {
    Array.from(queryRoot.querySelectorAll('.map--highlight')).forEach((path) =>
      path.classList.remove('map--highlight'),
    );
  };

  const onRemoveHighlight = () => {
    setHoveredElement(null);
    removeAllHighlights();
  };

  // Set tab order from top left to bottom right
  useEffect(() => {
    if (!query.data || !records?.length || selectedMuniTab === 'table') {
      return;
    }

    const bsfIds = records.map(({ bfs_number }) => bfs_number);

    const paths = queryRoot.getElementById('gemeinden');
    const includedBfsIds = [];

    if (paths && paths.children) {
      Array.from(paths.children)
        .filter((path) => {
          const bfsId = path.getAttribute('agis:gmdnr');

          // We only want one path for each municipality,
          // so municipalities with multiple paths like Baden are only focusable once.
          if (includedBfsIds.includes(bfsId) || !bsfIds.includes(bfsId)) {
            return false;
          }
          includedBfsIds.push(bfsId);
          return true;
        })
        .forEach((path, index, arr) => {
          const tabindex = arr.length - index;
          path.setAttribute('tabindex', tabindex.toString());
        });
    }
  }, [query.data, records, selectedMuniTab]);

  // Initialize map interactions and color
  useEffect(() => {
    if (!query.data || !records?.length || selectedMuniTab === 'table') {
      return;
    }

    const notFoundMunicipalities = [];

    records?.forEach((record) => {
      const bsfId = record.bfs_number;

      let municipalityElements: NodeListOf<SVGPathElement> = queryRoot.querySelectorAll(
        `[agis\\:gmdnr="${bsfId}"]`,
      );

      // We currently don't have the bfs number for all of the archive data,
      // try to match the name instead.
      if (!municipalityElements.length) {
        const municipalityName = unifyMuniciapalityName(record.name.de);
        municipalityElements = queryRoot.querySelectorAll(
          `[agis\\:gmdname="${municipalityName}"]`,
        );
      }

      if (municipalityElements.length) {
        const getColorFunction =
          type === 'poll' ? getMapColorPoll : getMapColorProportional;
        const { hasData, fillColor } = getColorFunction(record);

        if (!hasData) {
          return;
        }

        const onHighlightMunicipality = () => {
          const bfsNr = municipalityElements[0].getAttribute('agis:gmdnr');
          // add bfs number to record if not already present
          setHoveredElement({
            bfs_number: bfsNr,
            ...record,
          });
          removeAllHighlights();

          municipalityElements.forEach((municipalityElement) => {
            municipalityElement.classList.add('map--highlight');
            municipalityElement.parentElement?.appendChild(municipalityElement);
          });
        };

        municipalityElements.forEach((element) => {
          // avoid using fill and stroke attributes by using style
          // so that these settings can override base css styles
          element.setAttribute('style', `fill: ${fillColor};`);
          element.addEventListener('mouseenter', onHighlightMunicipality);
          element.classList.add('map--initialized');
          if (type === 'proportional') {
            element.addEventListener('click', () =>
              onSelectMunicipality(municipalityElements[0], record),
            );
          }
          element.addEventListener('mouseleave', onRemoveHighlight);

          if (selectedElement && selectedElement?.bfs_number === record?.bfs_number) {
            element.classList.add('map--selected');
          }

          // element.addEventListener('focus', onHighlightMunicipality);
          // element.addEventListener('blur', onRemoveHighlight);
        });
      } else {
        notFoundMunicipalities.push(record.name.de);
      }
    });

    const notFoundMunicipalitiesWithoutTotal = notFoundMunicipalities.filter(
      (municipality) => !municipality.startsWith('Total'),
    );
    if (notFoundMunicipalitiesWithoutTotal.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `Could not find ${notFoundMunicipalitiesWithoutTotal.length} municipalities: ${notFoundMunicipalitiesWithoutTotal}`,
      );
    }
  }, [records, query.data, selectedMuniTab]);

  // Map api returns doctype header resulting in excess characters being displayed.
  // These are removed here
  const cleanedMap = query.error ? null : query.data?.replace(']>', '');

  return {
    map: cleanedMap,
    isLoading: query.isLoading,
    hoveredElement,
    selectedElement,
    setSelectedElement,
  };
};

export default useMapApi;
